/**
 * @file The NotificationsService communicates with the notifications backend.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertsService } from '../core/alerts.service';
import { BreezeService } from '../core/breeze.service';
import { Request } from './request';
import { Recipient } from './recipient';
import { RequestStatus } from './request-status';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../settings.service';
import { AuthenticationService } from '../authentication.service';
import { EntityManagerService } from '../entity-manager.service';
import { ConfigurationsService } from '../configurations.service';
/**
 * The NotificationsService communicates with the notifications backend.
 * @export
 * @class NotificationsService
 * @extends {BreezeService}
 */
var NotificationsService = /** @class */ (function (_super) {
    tslib_1.__extends(NotificationsService, _super);
    /**
     * Creates an instance of NotificationsService.
     * @param {AlertsService} alertsService For user feedback messages.
     * @param {HttpClient} http Is used to get the un read count uri.
     * @param {OAuthService} oauthService Is needed while getting the un read count uri.
     */
    function NotificationsService(alertsService, translateService, http, authenticationService, settingsService, configurationsService, entityManagerService) {
        var _this = _super.call(this, alertsService, translateService, entityManagerService) || this;
        _this.http = http;
        _this.authenticationService = authenticationService;
        _this.configurationsService = configurationsService;
        // WARNING: Do not define an id property in the supported entity types.
        // Instead use the PROPERTY_ID constant.
        /**
         * Supported notifications entity types.
         * @private
         * @type {{ [key: string]: Function }}
         */
        _this.registerEntityTypes = {
            Request: Request,
            Recipient: Recipient
        };
        /**
         * Gets, sets the last count of unread incoming notifications of the current user.
         */
        _this.totalUnreadNotifications = 0;
        /**
         * This event will be triggered when the Unread count has changed
         */
        _this.unreadCountChanged = new EventEmitter();
        if (settingsService.settings.notifications.updateInterval > 0 && _this.configurationsService.hasNotifications()) {
            _this.unreadCountInterval = window.setInterval(function () {
                _this.updateUnreadCount();
            }, settingsService.settings.notifications.updateInterval * 1000); // 10000 = 10 seconden
        }
        _this.initialize('notifications');
        Object.keys(_this.registerEntityTypes).forEach(function (entityType) {
            _this.metadataStore.registerEntityTypeCtor(entityType, _this.registerEntityTypes[entityType]);
        });
        var domain = settingsService.settings.frontend.domains.find(function (item) { return item.name === 'notifications'; });
        if (domain) {
            _this.unreadCountUri = domain.uri + 'unreadcount';
        }
        else {
            console.warn('Notification domain could not found in the settings. New notifications won\'t pop up.');
        }
        return _this;
    }
    /**
     * Declines de aangeleverde requests
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.declineRequests = function (requests, save_callback, subscribe_callback) {
        requests.forEach(function (r) {
            if (!r.ReadOnly) {
                r.Status = RequestStatus.Decline;
            }
        });
        this.subscriptionPool.add(this.saveChanges(save_callback).subscribe(subscribe_callback));
    };
    /**
     * Declined het aangeleverde request
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.declineRequest = function (request, save_callback, subscribe_callback) {
        this.declineRequests([request], save_callback, subscribe_callback);
    };
    /**
     * Accepts de aangeleverde requests
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.acceptRequests = function (requests, save_callback, subscribe_callback) {
        requests.forEach(function (r) {
            if (!r.ReadOnly) {
                r.Status = RequestStatus.Accept;
            }
        });
        this.subscriptionPool.add(this.saveChanges(save_callback).subscribe(subscribe_callback));
    };
    /**
     * Accepts het aangeleverde request
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.acceptRequest = function (request, save_callback, subscribe_callback) {
        this.acceptRequests([request], save_callback, subscribe_callback);
    };
    /**
     * WithDraws de aangeleverde requests
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.withDrawRequests = function (requests, save_callback, subscribe_callback) {
        requests.forEach(function (r) {
            if (!r.ReadOnly) {
                r.Status = RequestStatus.Withdrawn;
            }
        });
        this.subscriptionPool.add(this.saveChanges(save_callback).subscribe(subscribe_callback));
    };
    /**
     * WithDraws het aangeleverde request
     * @param {[Request]} requests Requests die aangepast moeten worden
     * @param {(error: any) => void} save_callback Callback die wordt aangeroepen bij een save
     * @param {(value: SaveResult) => void} subscribe_callback Callback voor de subscribe op de SaveChanges
     */
    NotificationsService.prototype.withDrawRequest = function (request, save_callback, subscribe_callback) {
        this.withDrawRequests([request], save_callback, subscribe_callback);
    };
    /**
     * Marks the request as read or unread.
     * @param request The request is marked as read or unread.
     * @param value A value indicating whether the request is read or not.
     * @param configurationsService The configuration service.
     * @param save_callback The callback method for when the changes are saved.
     * @param subscribe_callback The subscribe callback method.
     */
    NotificationsService.prototype.setRequestReadValue = function (request, value, resourceOid, save_callback, subscribe_callback) {
        this.setRequestsReadValue([request], value, resourceOid, save_callback, subscribe_callback);
    };
    /**
     * Marks requests as read or unread.
     * @param request The requests are marked as read or unread.
     * @param value A value indicating whether the requests are read or not.
     * @param configurationsService The configuration service.
     * @param save_callback The callback method for when the changes are saved.
     * @param subscribe_callback The subscribe callback method.
     */
    NotificationsService.prototype.setRequestsReadValue = function (requests, value, userOid, save_callback, subscribe_callback) {
        var _this = this;
        requests.forEach(function (r) {
            if (r.IsRead !== value) {
                var requestStatus = r.UserRequestStatusItems.find(function (item) { return item.UserOid === userOid; });
                if (requestStatus) {
                    requestStatus.RequestIsRead = value;
                }
            }
        });
        this.subscriptionPool.add(this.saveChanges(save_callback)
            .subscribe(function (result) {
            subscribe_callback(result);
            _this.updateUnreadCount();
        }));
    };
    /**
     * Gets the number of unread notifications from the server.
     */
    NotificationsService.prototype.updateUnreadCount = function () {
        var _this = this;
        if (this.unreadCountUri && this.authenticationService.isAuthenticated()) {
            this.subscriptionPool.add(this.http.get(this.unreadCountUri).subscribe(function (value) {
                var newValue = value;
                if (newValue !== _this.totalUnreadNotifications) {
                    var previousCount = _this.totalUnreadNotifications;
                    _this.totalUnreadNotifications = newValue;
                    _this.unreadCountChanged.emit({ previous: previousCount, current: _this.totalUnreadNotifications });
                }
            }));
        }
    };
    /**
     * Trying to solve a non-reproduceable bug (not in development environment) issue 1319.
     * We did see a change set with more than one entity. There should be only one entity changed.
     * The valid entity in the change set was the last one.
     */
    NotificationsService.prototype.symptomManagementIssue1319 = function () {
        // Symptoom bestrijding voor niet-reproduceerbare bug.
        // Bij ruilen en waarnemen kunnen er bij specifieke gebruikers niet juiste requesten ontstaan.
        // Wanneer dit gebeurt is onduidelijk.
        // Ik ga ervan uit dat de laatste de juiste is.
        var changes = this.entityManager.getChanges();
        var index = changes.length - 1;
        while (index > 0) {
            index--;
            var entity = changes[index];
            if (entity.entityType.shortName === 'Request' && entity.entityAspect.entityState.isAdded()) {
                // Make it as specific for symptom issue 1319 as I can.
                entity.entityAspect.rejectChanges();
            }
        }
    };
    return NotificationsService;
}(BreezeService));
export { NotificationsService };
